import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Select } from '@/components';
import type { Order, OrderStatus, PrintStatus } from '@shared/types';
import { supabase } from '@/lib/supabase';
import { formatDateTime } from '@/utils/format';
import { downloadOrderAsZip, downloadOrderFilesIndividually } from '@/utils/downloadOrderZip';

// Temporarily hide the print-status selector. Flip to `true` to restore.
const SHOW_PRINT_STATUS = false;

export const OrderDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<OrderStatus>('pending');
  const [printStatus, setPrintStatus] = useState<PrintStatus>('pending');
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [isDownloadingEach, setIsDownloadingEach] = useState(false);
  const isDownloading = isDownloadingZip || isDownloadingEach;

  const handleDownloadZip = async () => {
    if (!order || isDownloading) return;
    setIsDownloadingZip(true);
    try {
      await downloadOrderAsZip(order);
    } catch (err) {
      console.error('Download all failed:', err);
      alert(err instanceof Error ? err.message : 'Failed to download files as ZIP.');
    } finally {
      setIsDownloadingZip(false);
    }
  };

  const handleDownloadEach = async () => {
    if (!order || isDownloading) return;
    setIsDownloadingEach(true);
    try {
      await downloadOrderFilesIndividually(order);
    } catch (err) {
      console.error('Download each failed:', err);
      alert(err instanceof Error ? err.message : 'Failed to download files.');
    } finally {
      setIsDownloadingEach(false);
    }
  };

  useEffect(() => {
    if (!id) return;

    const fetchOrder = async () => {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('orders')
        .select('id, shop_id, customer_phone, print_status, created_at, updated_at, order_details')
        .eq('id', id)
        .single();

      if (!error && data) {
        setOrder(data as Order);
        setPaymentStatus((data as Order).payment_status);
        setPrintStatus((data as Order).print_status ?? 'pending');
      }
      setIsLoading(false);
    };

    fetchOrder();
  }, [id]);

  const handleStatusChange = async (newStatus: OrderStatus) => {
    if (!order) return;
    setIsSaving(true);
    setPaymentStatus(newStatus);

    const { data, error } = await supabase
      .from('orders')
      .update({ payment_status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', order.id)
      .select()
      .single();

    if (!error && data) {
      setOrder(data as Order);
    } else if (error) {
      console.error('Failed to update status:', error);
      setPaymentStatus(order.payment_status); // revert
    }
    setIsSaving(false);
  };

  const handlePrintStatusChange = async (newStatus: PrintStatus) => {
    if (!order) return;
    setIsSaving(true);
    setPrintStatus(newStatus);

    const { data, error } = await supabase
      .from('orders')
      .update({ print_status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', order.id)
      .select()
      .single();

    if (!error && data) {
      setOrder(data as Order);
    } else if (error) {
      console.error('Failed to update print status:', error);
      setPrintStatus(order.print_status ?? 'pending'); // revert
    }
    setIsSaving(false);
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          <p className="text-sm text-slate-500">Loading order...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-6">
        <Card>
          <p className="text-gray-600">Order not found.</p>
          <div className="mt-4">
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-950">
              Order #{order.id.slice(0, 8)}
            </h1>
            <p className="text-sm text-slate-500 mt-1">{formatDateTime(order.created_at)}</p>
          </div>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            ← Back
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Customer */}
          <Card>
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase text-slate-500">Customer</p>
              <p className="font-medium text-slate-900">{order.customer_phone}</p>
              <p className="text-xs text-slate-500">Received</p>
              <p className="text-sm text-slate-700">{formatDateTime(order.created_at)}</p>
            </div>
          </Card>

          {/* Files */}
          <Card>
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase text-slate-500">
                  Files ({order.order_details?.length ?? 0})
                </p>
                {order.order_details?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleDownloadEach}
                      disabled={isDownloading}
                    >
                      {isDownloadingEach ? 'Downloading…' : 'Download Each'}
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleDownloadZip}
                      disabled={isDownloading}
                    >
                      {isDownloadingZip ? 'Zipping…' : 'Download All (ZIP)'}
                    </Button>
                  </div>
                )}
              </div>
              {order.order_details?.length > 0 ? (
                <ul className="space-y-2">
                   {order.order_details.map((file, i) => {
                     // Generate fresh URL using the new endpoint instead of storing expired pre-signed URLs
                     const freshFileUrl = `${import.meta.env.VITE_API_URL}/file-url/${order.shop_id}/${order.id}/${i}`;
                     
                     return (
                       <li key={i} className="flex items-center gap-2 text-sm">
                         <span className="text-slate-400">📄</span>
                         <a
                           href={freshFileUrl}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="truncate text-blue-600 hover:underline"
                           title={file.fileName}
                         >
                           {file.fileName}
                         </a>
                       </li>
                     );
                   })}
                </ul>
              ) : (
                <p className="text-sm text-slate-400">No files attached</p>
              )}
            </div>
          </Card>

          {/* Status */}
          <Card>
            <div className="space-y-3">
              {SHOW_PRINT_STATUS && (
                <>
                  <p className="text-xs font-semibold uppercase text-slate-500">Print Status</p>
                  <Select
                    id="print-status"
                    label=""
                    options={[
                      { value: 'pending', label: 'Not Printed' },
                      { value: 'printing', label: 'Printing' },
                      { value: 'printed', label: 'Printed' },
                    ]}
                    value={printStatus}
                    onChange={(e) => handlePrintStatusChange(e.target.value as PrintStatus)}
                    fullWidth
                    disabled={isSaving}
                  />
                  {printStatus === 'printed' && (
                    <p className="text-xs text-slate-500">
                      This order is printed and frozen. New files from this customer will
                      start a fresh order.
                    </p>
                  )}
                </>
              )}

              <p className="text-xs font-semibold uppercase text-slate-500 pt-2">Payment Status</p>
              <Select
                id="order-status"
                label=""
                options={[
                  { value: 'pending', label: 'Pending' },
                  { value: 'printing', label: 'Printing' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'failed', label: 'Failed' },
                  { value: 'archived', label: 'Archived' },
                ]}
                value={paymentStatus}
                onChange={(e) => handleStatusChange(e.target.value as OrderStatus)}
                fullWidth
                disabled={isSaving}
              />
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;
